import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, KeyRound, Plus, Trash2, UserPlus, Wand2, X } from "lucide-react";
import { Api, ApiError, type AuthUser, type Profile } from "../lib/api.js";

const MARKS = ["★", "☼", "❖", "✦", "⚜", "♛", "♞", "♜"];
const PIN_RE = /^\d{4,8}$/;

export function Profiles(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["profiles"], queryFn: Api.profiles.list });

  const [name, setName] = useState("");
  const [mark, setMark] = useState<string>(MARKS[0]!);

  const create = useMutation({
    mutationFn: () => Api.profiles.create({ display_name: name.trim(), forecourt_mark: mark }),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => Api.profiles.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Forecourt of the Stars</h1>
        <span className="section-label">{data?.profiles.length ?? 0} on file</span>
      </div>
      <div className="ornament-rule" />

      <form
        className="card p-5 flex flex-col sm:flex-row sm:items-end gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim().length > 0 && !create.isPending) create.mutate();
        }}
      >
        <div className="flex-1 w-full">
          <label className="section-label">Name on the plaque</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
            placeholder="Douglas"
          />
        </div>
        <div>
          <label className="section-label">Mark</label>
          <div className="mt-2 flex gap-1 flex-wrap">
            {MARKS.map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mark === m}
                onClick={() => setMark(m)}
                className="forecourt-mark w-9 h-9 text-xl aria-[pressed=true]:ring-2 aria-[pressed=true]:ring-goldHi"
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <button type="submit" className="pill self-start sm:self-auto" disabled={create.isPending || name.trim() === ""}>
          <Plus size={16} /> Add
        </button>
      </form>
      {create.isError && (
        <div className="mt-3 text-vermilionHi text-sm">
          {create.error instanceof ApiError ? create.error.message : "Could not add that profile."}
        </div>
      )}

      <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading ? (
          <>
            <li className="skeleton h-32" />
            <li className="skeleton h-32" />
            <li className="skeleton h-32" />
          </>
        ) : data?.profiles.length === 0 ? (
          <li className="col-span-full card p-10 text-parchment text-center font-display tracking-smallcaps">
            No names on the plaque yet.
          </li>
        ) : (
          data?.profiles.map((p) => <ProfileTile key={p.id} profile={p} onRemove={() => remove.mutate(p.id)} />)
        )}
      </ul>

      <AccountsSection />
    </div>
  );
}

function ProfileTile({ profile, onRemove }: { profile: Profile; onRemove: () => void }): JSX.Element {
  return (
    <li className="card p-6 flex items-center gap-4 group">
      <div className="forecourt-mark w-16 h-16 text-3xl shrink-0">
        {profile.forecourt_mark ?? profile.avatar_emoji ?? "★"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl text-ivory truncate">{profile.display_name}</div>
        <div className="text-[12px] text-parchment mt-1 tracking-smallcaps uppercase">
          {profile.onboarding_complete ? "Onboarded" : "Awaits onboarding"}
        </div>
      </div>
      <Link
        to={`/profiles/${profile.id}/onboarding`}
        className="pill"
        title={profile.onboarding_complete ? "Re-do onboarding" : "Start onboarding"}
      >
        <Wand2 size={14} />
        {profile.onboarding_complete ? "Refresh" : "Onboard"}
      </Link>
      <button
        onClick={onRemove}
        className="pill-ghost opacity-0 group-hover:opacity-100 transition"
        aria-label={`Remove ${profile.display_name}`}
      >
        <Trash2 size={16} />
      </button>
    </li>
  );
}

/**
 * Login accounts + their PINs. The PIN pad (Login.tsx) is the only way into
 * the app, so an account without a PIN can't sign in at all — this section
 * is where accounts are created and given one.
 */
function AccountsSection(): JSX.Element {
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ["auth-users"], queryFn: Api.auth.users.list });

  const [newUser, setNewUser] = useState("");
  const [newName, setNewName] = useState("");
  const [newPin, setNewPin] = useState("");

  const invalidate = (): void => void qc.invalidateQueries({ queryKey: ["auth-users"] });

  const addAccount = useMutation({
    mutationFn: () =>
      Api.auth.users.create({
        username: newUser.trim(),
        display_name: newName.trim() || undefined,
        pin: newPin.trim(),
      }),
    onSuccess: () => {
      setNewUser("");
      setNewName("");
      setNewPin("");
      invalidate();
    },
  });

  const setPin = useMutation({
    mutationFn: (args: { id: string; pin: string | null }) => Api.auth.users.setPin(args.id, args.pin),
    onSuccess: invalidate,
  });

  const addValid = newUser.trim().length >= 2 && PIN_RE.test(newPin.trim());

  return (
    <section className="mt-12">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-2xl text-ivory">Accounts &amp; PINs</h2>
        <span className="section-label">{usersQ.data?.users.length ?? 0} sign-ins</span>
      </div>
      <div className="ornament-rule" />
      <p className="text-parchment text-sm mb-5">
        The PIN pad on the sign-in screen is the only way in — an account without a PIN can&apos;t
        sign in. PINs are 4–8 digits and each must be unique.
      </p>

      <form
        className="card p-5 flex flex-col sm:flex-row sm:items-end gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (addValid && !addAccount.isPending) addAccount.mutate();
        }}
      >
        <div className="w-full sm:w-44">
          <label className="section-label">Username</label>
          <input
            value={newUser}
            onChange={(e) => setNewUser(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
            placeholder="georgia"
          />
        </div>
        <div className="flex-1 w-full">
          <label className="section-label">Display name</label>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoComplete="off"
            className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none"
            placeholder="Georgia"
          />
        </div>
        <div className="w-full sm:w-36">
          <label className="section-label">PIN</label>
          <input
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
            inputMode="numeric"
            autoComplete="off"
            className="mt-2 w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none tracking-[0.3em]"
            placeholder="4–8 digits"
          />
        </div>
        <button type="submit" className="pill self-start sm:self-auto" disabled={addAccount.isPending || !addValid}>
          <UserPlus size={16} /> Add account
        </button>
      </form>
      {addAccount.isError && (
        <div className="mt-3 text-vermilionHi text-sm">
          {addAccount.error instanceof ApiError ? addAccount.error.message : "Could not add that account."}
        </div>
      )}

      <ul className="mt-6 flex flex-col gap-3">
        {usersQ.isLoading ? (
          <li className="skeleton h-20" />
        ) : (
          usersQ.data?.users.map((u) => (
            <AccountRow
              key={u.id}
              user={u}
              busy={setPin.isPending}
              error={setPin.isError && setPin.error instanceof ApiError ? setPin.error.message : null}
              onSetPin={(pin) => setPin.mutate({ id: u.id, pin })}
            />
          ))
        )}
      </ul>
    </section>
  );
}

function AccountRow({
  user,
  busy,
  error,
  onSetPin,
}: {
  user: AuthUser;
  busy: boolean;
  error: string | null;
  onSetPin: (pin: string | null) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [pin, setPinLocal] = useState("");

  function save(): void {
    if (!PIN_RE.test(pin.trim()) || busy) return;
    onSetPin(pin.trim());
    setPinLocal("");
    setEditing(false);
  }

  return (
    <li className="card p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="forecourt-mark w-11 h-11 text-lg shrink-0">
          {(user.display_name ?? user.username).charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-ivory truncate">{user.display_name ?? user.username}</div>
          <div className="text-[12px] text-parchment tracking-smallcaps uppercase">
            @{user.username}
            {user.is_admin ? " · admin" : ""}
          </div>
        </div>
        <span
          className={`text-[12px] tracking-smallcaps uppercase ${
            user.has_pin ? "text-gold" : "text-vermilionHi"
          }`}
        >
          {user.has_pin ? "PIN set" : "No PIN — cannot sign in"}
        </span>
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              value={pin}
              onChange={(e) => setPinLocal(e.target.value.replace(/\D/g, "").slice(0, 8))}
              inputMode="numeric"
              autoFocus
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
              className="w-28 bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory focus:border-gold outline-none tracking-[0.3em]"
              placeholder="4–8"
            />
            <button onClick={save} disabled={busy || !PIN_RE.test(pin.trim())} className="pill" aria-label="Save PIN">
              <Check size={14} />
            </button>
            <button onClick={() => setEditing(false)} className="pill-ghost" aria-label="Cancel">
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} className="pill">
              <KeyRound size={14} />
              {user.has_pin ? "Change PIN" : "Set PIN"}
            </button>
            {user.has_pin && (
              <button onClick={() => onSetPin(null)} disabled={busy} className="pill-ghost">
                Clear
              </button>
            )}
          </div>
        )}
      </div>
      {error && <div className="mt-2 text-vermilionHi text-sm">{error}</div>}
    </li>
  );
}
